import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ['toggle', 'button']

  connect() {
    // INTENT: Initialize timer references for debounced show and hide operations.
    // EXPECTED OUTCOME: Tracks active timeout IDs to prevent race conditions during rapid user interactions.
    // CAVEAT/TRAP: Always initialize to null so clearTimeout checks are safe.
    this.showTimer = null
    this.hideTimer = null
  }

  disconnect() {
    // INTENT: Clean up any active timers when controller unmounts from DOM.
    // EXPECTED OUTCOME: Prevents memory leaks and callbacks firing on detached DOM nodes.
    // CAVEAT/TRAP: Stimulus disconnect lifecycle must always clear asynchronous handlers.
    this.clearTimers()
  }

  clearTimers() {
    // INTENT: Cancel all pending show/hide timers before starting a new state transition.
    // EXPECTED OUTCOME: Guarantees only the latest requested state transition executes.
    // CAVEAT/TRAP: If a hide timer is running while show is requested, failing to cancel hide will collapse the menu right after opening.
    if (this.showTimer) {
      clearTimeout(this.showTimer)
      this.showTimer = null
    }
    if (this.hideTimer) {
      clearTimeout(this.hideTimer)
      this.hideTimer = null
    }
  }

  show() {
    this.clearTimers()
    this.showTimer = setTimeout(() => {
      this.toggleTargets.forEach((target) => {
        target.classList.remove('hidden')
        target.classList.add('animate-fade-in-down')
      })
      this.buttonTargets.forEach((button) => {
        button.setAttribute('aria-expanded', 'true')
      })
    }, 150);
  }

  hide(event) {
    // INTENT: Dismiss dropdown when focus or pointer leaves the dropdown component entirely.
    // EXPECTED OUTCOME: Adds hidden class to toggle target and resets button aria-expanded state to false.
    // CAVEAT/TRAP: If focusout relatedTarget is another element inside this controller (such as menu links), do not hide.
    if (event?.relatedTarget && this.element.contains(event.relatedTarget)) {
      return
    }

    this.clearTimers()
    this.hideTimer = setTimeout(() => {
      this.toggleTargets.forEach((target) => {
        target.classList.add('hidden')
      })
      this.buttonTargets.forEach((button) => {
        button.setAttribute('aria-expanded', 'false')
      })
    }, 300);
  }

  toggle() {
    this.clearTimers()
    setTimeout(() => {
      this.toggleTargets.forEach((target) => {
        target.classList.toggle('hidden')
        target.classList.toggle('animate-fade-in-down')
      })
      this.buttonTargets.forEach((button) => {
        const isExpanded = button.getAttribute('aria-expanded') === 'true'
        button.setAttribute('aria-expanded', isExpanded ? 'false' : 'true')
      })
    }, 300);
  }

  click(event) {
    // INTENT: Handle mouse and keyboard click actions on the dropdown trigger button, specifically resolving desktop Safari focus behavior.
    // EXPECTED OUTCOME: In macOS Safari (where native buttons do not focus on click), explicitly focuses the button and opens the menu, or toggles it closed if already open.
    // CAVEAT/TRAP: Must check if button is already expanded to toggle closed rather than re-triggering show.
    const button = event?.currentTarget || this.buttonTargets[0]
    if (button) {
      if (button.getAttribute('aria-expanded') === 'true') {
        this.hide()
      } else {
        button.focus()
        this.show()
      }
    }
  }

  escape(event) {
    // INTENT: Allow dismissing open dropdown via standard Escape key press per WCAG 2.1.1 keyboard patterns.
    // EXPECTED OUTCOME: Closes menu immediately, sets aria-expanded=false, and returns keyboard focus to the trigger button.
    // CAVEAT/TRAP: Only dismiss if dropdown is currently expanded; otherwise let event bubble naturally.
    const button = this.buttonTargets[0]
    if (button && button.getAttribute('aria-expanded') === 'true') {
      this.clearTimers()
      this.toggleTargets.forEach((target) => {
        target.classList.add('hidden')
      })
      button.setAttribute('aria-expanded', 'false')
      button.focus()
    }
  }

  /* 
    iOS Safari may not reliably focus non-interactive or pseudo-button elements on tap,
    so we explicitly focus the target here for consistent behavior across browsers.
  */
  touch(event) {
    const target = event?.currentTarget || this.buttonTargets[0]

    if (target) {
      target.focus()
    }
  }
}
